import { pwnedPrefix, isPwned, PWNED_RANGE_BATCH_SIZE } from '@heybesafe/shared';
import { sha1Hex } from '../crypto/sha1Hex';
import { fetchPwnedRanges } from '../api/pwnedRange';
import { getVerdict, setVerdict, isFresh, type Verdict } from './checkupCache';

export interface ExposedResult {
  /** item id → breach count (prevalence), for exposed logins only. */
  exposedById: Map<string, number>;
  /** True when the check could not run at all (every range fetch it attempted
   *  failed) — the UI should show "couldn't check", never a false "safe". A
   *  partial failure, or an all-cache-hit run with nothing to fetch, is not
   *  `failed`. */
  failed: boolean;
}

export interface ExposedDeps {
  sha1Hex: typeof sha1Hex;
  fetchPwnedRanges: typeof fetchPwnedRanges;
  /** Prefixes per batch request (tests shrink it). */
  batchSize: number;
  /** Verdict cache (injectable for tests). Defaults to the real localStorage
   *  cache, so a fresh verdict skips the network entirely. */
  getVerdict: (itemId: string) => Verdict | null;
  setVerdict: (itemId: string, v: Verdict) => void;
  isFresh: (v: Verdict, itemUpdatedAt?: string) => boolean;
  now: () => number;
}

const DEFAULT_DEPS: ExposedDeps = {
  sha1Hex,
  fetchPwnedRanges,
  batchSize: PWNED_RANGE_BATCH_SIZE,
  getVerdict,
  setVerdict,
  isFresh,
  now: Date.now,
};

type CheckupLogin = { id: string; password?: string | null; updatedAt?: string };

/** Max batch requests in flight at once. */
export const MAX_CONCURRENT_BATCHES = 2;

/**
 * Fetch every prefix's range in `POST /pwned-range` batches of `batchSize`, at
 * most [MAX_CONCURRENT_BATCHES] in flight, and start no new batch once one has
 * failed outright. A whole-batch failure is almost always the WAF rate-limiting
 * this IP, and pressing on only keeps the IP locked out (the 2026-10-16
 * lockout). Prefixes never fetched — or null within a batch (one upstream miss)
 * — are absent/null → unknown, not cached, retried next run.
 */
async function fetchRanges(
  prefixes: ReadonlyArray<string>,
  fetchBatch: (prefixes: ReadonlyArray<string>) => Promise<Map<string, string | null> | null>,
  batchSize: number,
): Promise<Map<string, string | null>> {
  const batches: string[][] = [];
  for (let i = 0; i < prefixes.length; i += batchSize) batches.push(prefixes.slice(i, i + batchSize));

  const ranges = new Map<string, string | null>();
  let next = 0;
  let stop = false;
  const worker = async () => {
    while (!stop && next < batches.length) {
      const result = await fetchBatch(batches[next++]);
      if (result == null) {
        stop = true;
        continue;
      }
      for (const [p, range] of result) ranges.set(p, range);
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_BATCHES, batches.length) }, worker));
  return ranges;
}

/**
 * Run the HIBP k-anonymity "exposed" check over the given logins, entirely with
 * the shared pure halves (`pwnedPrefix` / `isPwned`). Only 5-char prefixes are
 * ever sent (batched by `fetchPwnedRanges`); the password and its full hash
 * never leave the device.
 *
 * **Cached:** a login whose verdict is still fresh (within the re-check window
 * and unchanged since) is served from cache with NO network call — so the
 * checkup hits HIBP for a given password at most about once a week. Only
 * stale / never-checked / recently-edited logins are fetched, and those fetches
 * are deduped by prefix. Freshly-computed verdicts (clean or exposed) are cached.
 */
export async function findExposed(
  logins: ReadonlyArray<CheckupLogin>,
  deps: Partial<ExposedDeps> = {},
): Promise<ExposedResult> {
  const d = { ...DEFAULT_DEPS, ...deps };
  const exposedById = new Map<string, number>();
  const toCheck: { id: string; prefix: string; suffix: string }[] = [];

  for (const login of logins) {
    if (!login.password) continue;
    const cached = d.getVerdict(login.id);
    if (cached && d.isFresh(cached, login.updatedAt)) {
      if (cached.exposed) exposedById.set(login.id, cached.count);
      continue; // fresh cache → no network
    }
    const { prefix, suffix } = pwnedPrefix(await d.sha1Hex(login.password));
    toCheck.push({ id: login.id, prefix, suffix });
  }

  const prefixes = [...new Set(toCheck.map((e) => e.prefix))];
  const ranges = await fetchRanges(prefixes, d.fetchPwnedRanges, d.batchSize);

  for (const e of toCheck) {
    const range = ranges.get(e.prefix);
    if (range == null) continue; // fetch failed — don't cache a false "clean"
    const count = isPwned(range, e.suffix);
    d.setVerdict(e.id, { exposed: count > 0, count: count > 0 ? count : 0, checkedAt: d.now() });
    if (count > 0) exposedById.set(e.id, count);
  }

  const failed = toCheck.length > 0 && toCheck.every((e) => ranges.get(e.prefix) == null);
  return { exposedById, failed };
}
